// Request contracts mirror CommerceFulfillment / CommerceTracking in Commerce.
export const MANUAL_STATUSES = ['pending', 'label_created', 'in_transit', 'delivered', 'exception', 'returned']
// Only offer existing form contracts; an unknown adapter needs its own request contract.
const SUPPORTED_PROVIDERS = ['manual', 'shippo', '17track', 'test-ship']
export function configuredLogisticsProviders(health) {
  return [...new Set(health?.logisticsProviders || [])].filter(id => SUPPORTED_PROVIDERS.includes(id))
}

export function remainingItems(aggregate) {
  return (aggregate?.order?.items || []).map(item => {
    const shipped = (aggregate.fulfillments || []).reduce((total, shipment) => total + (shipment.items || [])
      .filter(row => row.orderItemId === item.id)
      .reduce((quantity, row) => quantity + row.quantity, 0), 0)
    return { ...item, quantity: Math.max(0, item.quantity - shipped) }
  })
}

export const canFulfill = (order) => ['paid', 'partially_refunded', 'fulfillment'].includes(order?.status)

export function fulfillmentItems(order, quantities) {
  const items = (order?.items || []).map((item) => {
    const quantity = Number(quantities[item.id] || 0)
    if (!Number.isInteger(quantity) || quantity < 0 || quantity > item.quantity) throw new Error('Invalid item quantity / 商品数量无效')
    return { orderItemId: item.id, quantity }
  }).filter((item) => item.quantity > 0)
  if (!items.length) throw new Error('Select at least one item / 请至少选择一个商品')
  return items
}

export const CUSTOMS_CONTENT_TYPES = ['DOCUMENTS', 'GIFT', 'SAMPLE', 'MERCHANDISE', 'HUMANITARIAN_DONATION', 'RETURN_MERCHANDISE', 'OTHER']
export const CUSTOMS_INCOTERMS = ['DDP', 'DDU', 'FCA', 'DAP', 'eDAP']
export const CUSTOMS_EEL = ['NOEEI_30_37_a', 'NOEEI_30_37_h', 'NOEEI_30_37_f', 'NOEEI_30_36', 'AES_ITN']
export function shipmentIsInternational(sender, recipient) {
  const from = String(sender?.country || sender?.countryCode || '').trim().toUpperCase()
  const to = String(recipient?.country || recipient?.countryCode || '').trim().toUpperCase()
  return from && to ? from !== to : null
}

export function customsDeclarationBody(form) {
  const required = (value, max, label) => {
    const result = String(value ?? '').trim()
    if (!result || result.length > max) throw new Error(`Invalid customs ${label} / 报关字段无效: ${label}`)
    return result
  }
  const decimal = (value, label) => {
    const result = required(value, 30, label)
    if (!/^\d+(?:\.\d{1,4})?$/.test(result) || Number(result) <= 0 || Number(result) > 1_000_000_000) throw new Error(`Invalid customs ${label}`)
    return result
  }
  if (form?.mode === 'existing') return required(form.objectId, 200, 'declaration ID')
  if (form?.certify !== true) throw new Error('Certify the customs declaration / 请确认报关信息真实准确')
  if (!CUSTOMS_CONTENT_TYPES.includes(form.contentsType) || !CUSTOMS_INCOTERMS.includes(form.incoterm) || !['RETURN', 'ABANDON'].includes(form.nonDeliveryOption)) throw new Error('Select customs contents, incoterm and non-delivery option / 请选择报关类型、贸易条款及无法投递处理方式')
  const result = { certify: true, certifySigner: required(form.certifySigner, 200, 'signer'), contentsType: form.contentsType, nonDeliveryOption: form.nonDeliveryOption, incoterm: form.incoterm }
  if (form.contentsType === 'OTHER' || form.contentsExplanation?.trim()) result.contentsExplanation = required(form.contentsExplanation, 500, 'contents explanation')
  if (!Array.isArray(form.items) || !form.items.length) throw new Error('Add customs items / 请添加报关商品')
  result.items = form.items.map((item) => {
    const quantity = Number(item.quantity)
    if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > 1_000_000) throw new Error('Invalid customs quantity')
    if (!['g', 'kg', 'lb', 'oz'].includes(item.massUnit)) throw new Error('Invalid customs mass unit')
    const valueCurrency = required(item.valueCurrency, 3, 'currency').toUpperCase()
    const originCountry = required(item.originCountry, 2, 'origin country').toUpperCase()
    if (!/^[A-Z]{3}$/.test(valueCurrency) || !/^[A-Z]{2}$/.test(originCountry)) throw new Error('Invalid customs currency or origin country')
    const entry = { description: required(item.description, 200, 'description'), quantity, netWeight: decimal(item.netWeight, 'net weight'), massUnit: item.massUnit, valueAmount: decimal(item.valueAmount, 'value amount'), valueCurrency, originCountry }
    for (const [key, max] of [['tariffNumber', 12], ['hsCode', 50], ['skuCode', 100], ['eccnEar99', 100]]) {
      if (item[key]?.trim()) {
        entry[key] = required(item[key], max, key)
        if (['tariffNumber', 'hsCode'].includes(key) && !/^[A-Za-z0-9. -]+$/.test(entry[key])) throw new Error(`Invalid customs ${key}`)
      }
    }
    return entry
  })
  if (form.eelPfc) {
    if (!CUSTOMS_EEL.includes(form.eelPfc)) throw new Error('Invalid customs EEL/PFC')
    result.eelPfc = form.eelPfc
    if (form.eelPfc === 'AES_ITN') result.aesItn = required(form.aesItn, 100, 'AES ITN')
  }
  if (form.invoice?.trim()) result.invoice = required(form.invoice, 15, 'invoice')
  result.commercialInvoice = form.commercialInvoice === true
  return result
}

export function quoteBody(addressFrom, parcel, trade) {
  for (const field of ['name', 'street1', 'city', 'zip', 'country']) {
    if (!addressFrom[field]?.trim()) throw new Error('Complete sender address / 请填写完整发件地址')
  }
  if (!/^[A-Z]{2}$/.test(addressFrom.country)) throw new Error('Use a two-letter country code / 请填写两位国家代码')
  for (const field of ['length', 'width', 'height', 'weight']) {
    if (!Number.isFinite(Number(parcel[field])) || Number(parcel[field]) <= 0) throw new Error('Invalid parcel dimensions or weight / 包裹尺寸或重量无效')
  }
  const body = { provider: 'shippo', addressFrom: { ...addressFrom }, parcels: [{ ...parcel }] }
  if (trade) {
    const international = shipmentIsInternational(addressFrom, trade.shippingAddress)
    if (international === null) throw new Error('Order recipient country is missing; correct the order address first / 订单收件国家缺失，请先修正订单地址')
    if (international) {
      if (!addressFrom.phone?.trim() || !trade.shippingAddress?.phone?.trim()) throw new Error('International shipments require sender and recipient phone numbers / 国际物流需要发件人和收件人电话')
      body.international = true
      body.customsDeclaration = customsDeclarationBody(trade.customs)
    }
  }
  return body
}

export function fulfillmentBody({ order, quantities, provider, mode, carrier, trackingNumber, quote, rateId, confirmed }) {
  if (!canFulfill(order)) throw new Error('Order is not eligible for fulfillment / 订单不可履约')
  if (!SUPPORTED_PROVIDERS.includes(provider)) throw new Error('Select a configured provider / 请选择已配置服务商')
  const items = fulfillmentItems(order, quantities)
  if (mode === 'label') {
    const rate = quote?.rates?.find((item) => item.id === rateId)
    if (provider !== 'shippo' || !quote?.shipmentId || !rate) throw new Error('Get a quote and select a rate / 请获取报价并选择费率')
    if (!confirmed) throw new Error('Confirm label purchase first / 请先确认购买面单')
    return { provider, items, shipmentId: quote.shipmentId, rateId, selectedRate: rate, labelFileType: 'PDF_4x6', ...(quote.international === true ? { international: true, ...(quote.customs ? { customsSummary: quote.customs, ...(quote.customs.incoterm ? { incoterm: quote.customs.incoterm } : {}) } : {}) } : {}) }
  }
  if (provider === 'test-ship') return { provider, items }
  if (provider === '17track' && carrier.trim() && !/^[1-9][0-9]{0,8}$/.test(carrier.trim())) throw new Error('17TRACK requires a numeric carrier ID or leave it blank for detection')
  if (!trackingNumber.trim() || (provider !== '17track' && !carrier.trim())) throw new Error('Enter carrier and tracking number / 请填写承运商和物流单号')
  return { provider, items, ...(carrier.trim() ? { carrier: carrier.trim() } : {}), trackingNumber: trackingNumber.trim() }
}

export function manualPackages(aggregate) {
  return (aggregate?.fulfillments || []).filter((item) => item.provider === 'manual')
    .flatMap((item) => item.packages || []).filter((item) => item.id)
}

export function providerPackages(aggregate, providers) {
  return (aggregate?.fulfillments || []).filter(item => item.provider !== 'manual' && providers.includes(item.provider))
    .flatMap(item => (item.packages || []).filter(parcel => parcel.id).map(parcel => ({ ...parcel, provider: item.provider })))
}

export function manualTrackingBody(status, description, occurredAt = new Date().toISOString()) {
  if (!MANUAL_STATUSES.includes(status)) throw new Error('Invalid tracking status')
  return { provider: 'manual', status, events: [{ status, description: description.trim() || status, occurredAt }] }
}

// Keep both the key and body (including event timestamp) after ambiguous failures.
// An unresolved path must never accept changed input under a fresh key.
export function createLogisticsOperations(makeKey = () => crypto.randomUUID()) {
  const operations = new Map()
  let busy = false
  return {
    async run(request, path, input, makeBody = () => input) {
      if (busy) throw new Error('An operation is already in progress')
      const fingerprint = JSON.stringify([path, input])
      const pending = operations.get(path)
      if (pending && pending.fingerprint !== fingerprint) {
        throw new Error('Previous operation outcome is unknown. Check the order and retry the original unchanged inputs; a new operation is blocked / 上次操作结果不明，请核实订单或按原输入重试，禁止新操作')
      }
      let operation = pending?.operation
      if (!operation) {
        operation = { method: 'POST', body: makeBody(), idempotencyKey: makeKey() }
        operations.set(path, { fingerprint, operation })
      }
      busy = true
      try {
        const result = await request(path, operation)
        operations.delete(path)
        return result
      } catch (cause) {
        // Timeout, conflict/in-progress, throttling and malformed responses may
        // conceal a completed provider operation; keep the original key/body.
        const status = Number(cause?.status)
        if (status >= 400 && status < 500 && ![408, 409, 425, 429].includes(status) && cause?.code !== 'invalid_response') {
          operations.delete(path)
        }
        throw cause
      } finally { busy = false }
    },
  }
}
